/**
 * MapSearchControls
 * 
 * @atomic - Handles map search controls (location, entity, radius)
 * @idempotent - Pure component with React.memo
 * @modular - Self-contained search interface
 * 
 * Purpose: Provides unified search controls for location and entity search
 * Props: All search-related handlers and state
 * Returns: Search input row with location and entity search
 */

import { memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import SearchLocationInput from './SearchLocationInput'
import EntitySearchInput from './EntitySearchInput'

const MapSearchControls = memo(function MapSearchControls({
  // Location search props
  searchLocation,
  onSearchLocationChange,
  radius,
  onRadiusChange,
  onGeocode,
  onCurrentLocation,
  isGeocoding,
  locationSource,
  
  // Entity search props
  entitySearchQuery,
  onEntitySearchChange,
  entitySearchRef,
  isSearching,
  searchResults,
  showDropdown,
  onSelectEntity,
  onCloseDropdown,
  showNoResults,
  userLocation,
  searchLocationName = '',
  
  // Optional tabs
  immersiveTabs = null,
  isImmersiveMode = false,
  
  // Optional className
  className = ''
}) {
  const handleClearLocation = useCallback(() => {
    if (onSearchLocationChange) onSearchLocationChange('')
  }, [onSearchLocationChange])

  return (
    <div className={`flex flex-col sm:flex-row gap-3 ${className}`}>
      {/* Tab Navigation - Only in immersive mode */}
      {isImmersiveMode && immersiveTabs && (
        <div className="flex items-center">
          {immersiveTabs}
        </div>
      )}
      
      {/* Location input with radius selector */}
      <SearchLocationInput
        searchLocation={searchLocation}
        onSearchLocationChange={onSearchLocationChange}
        radius={radius}
        onRadiusChange={onRadiusChange}
        onGeocode={onGeocode}
        onCurrentLocation={onCurrentLocation}
        isGeocoding={isGeocoding}
        locationSource={locationSource}
        className="sm:w-auto"
      />

      {/* Entity search input takes remaining space */}
      <div className="flex-1">
        <EntitySearchInput
          value={entitySearchQuery}
          onChange={onEntitySearchChange}
          onFocus={() => searchResults.length > 0 && showDropdown}
          inputRef={entitySearchRef}
          isSearching={isSearching}
          searchResults={searchResults}
          showDropdown={showDropdown}
          onSelectEntity={onSelectEntity}
          onCloseDropdown={onCloseDropdown}
          showNoResults={showNoResults}
          userLocation={userLocation}
          searchLocation={searchLocation}
          searchLocationName={searchLocationName}
          radius={radius}
          onClearLocation={handleClearLocation}
          size="default"
        />
      </div>
    </div>
  )
})

export default MapSearchControls
